import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import { ArrowRight, Mic, BarChart3, Globe2, Sparkles, Shield } from "lucide-react";
import { PLATFORM_WALKTHROUGH } from "../lib/knowledgeBase";

const HERO_BG = "/hero-bg.svg";
const WAVE_IMG = "https://static.prod-images.emergentagent.com/jobs/0d7d2775-f8b2-45eb-9baf-72540d32168c/images/51a7e4796cb4d13d962bc55878c82b78bf7203c3b820194a1b26c152ef4ca897.png";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navigation />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[var(--rl-border)]">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="grain-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-32 lg:pb-40">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 px-3 py-1 border border-[var(--rl-border)] text-xs uppercase tracking-[0.25em] font-bold bg-white" data-testid="hero-badge">
                <span className="w-2 h-2 rounded-full bg-[var(--rl-success)] animate-pulse" />
                Live Voice Research • PIPEDA Compliant
              </span>
              <div className="mt-6 text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]" data-testid="hero-eyebrow">
                Consumer Insights
              </div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] mt-3" data-testid="hero-headline">
                Qualitative <span className="text-[var(--rl-brand)]">depth</span>.<br/>Quantitative <span className="text-[var(--rl-accent)]">scale</span>.<br/>In days, not weeks.
              </h1>
              <p className="mt-8 text-lg lg:text-xl text-[var(--rl-text-secondary)] max-w-2xl leading-relaxed font-medium" data-testid="hero-subheadline">
                Conduct automated voice and video participant interviews with dynamic, real-time moderation. Instantly surface high-fidelity trends, acoustic emotional themes, and authentic panelist quotes directly from raw feedback to power fast, validated strategic decisions.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4" data-testid="hero-cta-group">
                <Link
                  to="/register/client"
                  className="group inline-flex items-center justify-center gap-2 bg-[var(--rl-text)] text-white px-7 py-4 font-semibold text-base hover:bg-[var(--rl-brand)] transition-colors"
                  data-testid="hero-client-cta"
                >
                  Launch a Study
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="mt-10" data-testid="hero-pricing-block">
                <div className="font-display font-black text-3xl sm:text-4xl tracking-tight text-[var(--rl-text)]" data-testid="hero-price-headline">
                  Voice-first research that scales.
                </div>
                <p className="mt-2 text-sm text-[var(--rl-text-secondary)] leading-relaxed max-w-xl" data-testid="hero-price-subheadline">
                  Automated AI voice + video interviewing, guaranteed panelist rewards, and instant executive PPTX reporting — all locked into a transparent per-complete rate at checkout.
                </p>
                <div className="mt-5 flex items-center gap-6 text-xs uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)]">
                  <span>5-min interviews</span>
                  <span className="w-1 h-1 bg-[var(--rl-text-secondary)] rounded-full" />
                  <span>Atlantic Canada first</span>
                  <span className="w-1 h-1 bg-[var(--rl-text-secondary)] rounded-full" />
                  <span>No subscriptions</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative bg-[var(--rl-bg-inverse)] p-8 lg:p-10 border border-black/10">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/50 mb-4">Live Pulse Stream</div>
                <div className="space-y-4">
                  {[
                    { src: "INTERNAL", text: "The new packaging feels more premium but the price gap is hard to justify…" },
                    { src: "DEDICATED PANEL", text: "I rarely shop at Sobeys lately — drive-times have become an issue since the move." },
                    { src: "INTERCEPT", text: "Honestly the loyalty program is the only reason I keep coming back." },
                  ].map((q) => (
                    <div key={q.src} className="border-l-2 border-[var(--rl-accent)] pl-4 py-1">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-accent)]">{q.src}</div>
                      <div className="text-sm text-white/90 mt-1 leading-relaxed">"{q.text}"</div>
                    </div>
                  ))}
                </div>
                <img src={WAVE_IMG} alt="" className="mt-8 w-full opacity-60" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 3-Step Platform Walkthrough Teaser ===== */}
      <section className="border-b border-[var(--rl-border)] bg-white" data-testid="walkthrough-section">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-3xl">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]" data-testid="walkthrough-eyebrow">
                3-Step Platform Walkthrough
              </div>
              <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter leading-[1.05] mt-4" data-testid="walkthrough-headline">
                From brief to <span className="text-[var(--rl-brand)]">automated incentive fulfillment</span> and <span className="text-[var(--rl-accent)]">emotion mapping</span>, in days.
              </h2>
            </div>
            <Link
              to="/resources"
              className="self-start lg:self-end inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-6 py-3 text-sm font-semibold hover:bg-[var(--rl-brand)] transition-colors shrink-0"
              data-testid="walkthrough-cta"
            >
              Read the full playbook <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-12 grid sm:grid-cols-3 gap-4 lg:gap-5" data-testid="walkthrough-grid">
            {PLATFORM_WALKTHROUGH.map((s, i) => (
              <article
                key={s.n}
                className="group relative bg-white border border-[var(--rl-border)] p-7 lg:p-8 hover:border-[var(--rl-brand)] transition-colors"
                data-testid={`walkthrough-step-${i + 1}`}
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--rl-bg-subtle)] overflow-hidden">
                  <div className="h-full bg-[var(--rl-brand)] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                </div>
                <div className="flex items-baseline justify-between">
                  <div className="font-display font-black text-5xl lg:text-6xl tracking-tighter text-[var(--rl-brand)]" data-testid={`walkthrough-step-${i + 1}-num`}>
                    {s.n}
                  </div>
                  {i < PLATFORM_WALKTHROUGH.length - 1 && (
                    <ArrowRight className="hidden sm:block w-5 h-5 text-[var(--rl-border)] group-hover:text-[var(--rl-brand)] transition-colors" />
                  )}
                </div>
                <h3 className="mt-4 font-display font-black text-xl tracking-tight text-[var(--rl-text)]" data-testid={`walkthrough-step-${i + 1}-title`}>
                  {s.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--rl-text-secondary)]" data-testid={`walkthrough-step-${i + 1}-body`}>
                  {s.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Three sources */}
      <section className="border-b border-[var(--rl-border)]" data-testid="sources-section">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Multi-source Supply Engine</div>
            <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tight mt-4">Three routes to your respondent.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-[var(--rl-border)] mt-12 border border-[var(--rl-border)]">
            {[
              {
                tag: "Source 01",
                title: "Internal Panel",
                copy: "Auto-dispatch SMS + email to a vetted Canadian panel matching your demographic targets in seconds.",
                icon: Globe2,
              },
              {
                tag: "Source 02",
                title: "Programmatic Panel Networks",
                copy: "Authenticated quota requests pipe global respondents in, with currency-masked rewards and HMAC-secured postbacks.",
                icon: Sparkles,
              },
              {
                tag: "Source 03",
                title: "Direct Distribution",
                copy: "Generate a clean direct-distribution link to blast through your own customer list or embed in live channels.",
                icon: ArrowRight,
              },
            ].map((s, i) => (
              <div key={s.title} className="bg-white p-8 lg:p-10" data-testid={`source-card-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">{s.tag}</span>
                  <s.icon className="w-5 h-5 text-[var(--rl-brand)]" />
                </div>
                <h3 className="font-display font-bold text-2xl mt-6">{s.title}</h3>
                <p className="text-sm text-[var(--rl-text-secondary)] mt-4 leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Voice + reports */}
      <section className="border-b border-[var(--rl-border)]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-accent)]">Voice-first AI Interviewer</div>
            <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tight mt-4">A conversational interview that adapts to every panelist.</h2>
            <ul className="mt-8 space-y-5 text-base">
              {[
                ["Real-time waveform", "Browser AnalyserNode drives a true amplitude-reactive visualization while the user speaks."],
                ["Adaptive probing", "GPT-5.2 generates one tone-aware follow-up per topic — frustration, hesitation, enthusiasm all detected."],
                ["Anti-fraud layer", "Copy-paste blocked, tab-blur logged, sub-15-char answers gently rejected, fraud-strikes terminate."],
                ["Mobile resilience", "Client-side audio compression + IndexedDB buffer auto-syncs on network drops."],
              ].map(([t, d]) => (
                <li key={t} className="grid grid-cols-[40px_1fr] gap-4">
                  <Mic className="w-6 h-6 text-[var(--rl-brand)] mt-1" />
                  <div>
                    <div className="font-semibold">{t}</div>
                    <div className="text-sm text-[var(--rl-text-secondary)] mt-1 leading-relaxed">{d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-accent)]">Executive Visual Reporting</div>
            <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tight mt-4">From transcripts to a boardroom deck in one click.</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                ["Traffic Mix", "Channel-segmented completes (Internal / Programmatic / Intercept)"],
                ["Quant Charts", "Auto-built pie & bar charts across all multiple-choice prompts"],
                ["Thematic Clusters", "AI-grouped semantic themes, e.g. \"40% mention pricing friction\""],
                ["Traceable Quotes", "Click any theme to reveal verbatim anonymized quotes"],
                ["TL;DR Narrative", "Three executive bullets — written by GPT-5.2 against the transcript"],
                ["PPTX Export", "Brand-aligned 5-slide editable PowerPoint, ready for your boardroom"],
              ].map(([t, d], i) => (
                <div key={t} className="border border-[var(--rl-border)] p-5 hover:border-[var(--rl-brand)] transition-colors" data-testid={`feature-tile-${i}`}>
                  <BarChart3 className="w-5 h-5 text-[var(--rl-brand)]" />
                  <div className="font-semibold mt-3">{t}</div>
                  <div className="text-xs text-[var(--rl-text-secondary)] mt-1 leading-relaxed">{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[var(--rl-bg-inverse)] text-white" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-white flex items-center justify-center">
                <span className="text-[var(--rl-brand)] font-display font-black text-sm">AI</span>
              </div>
              <span className="font-display font-black text-lg">Adrigo Insights<span className="text-[var(--rl-accent)]"> AI</span></span>
            </div>
            <p className="text-sm text-white/60 mt-4 leading-relaxed max-w-xs">Atlantic Canada's voice-first qualitative research engine.</p>
          </div>
          <div>
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-[var(--rl-accent)] flex-shrink-0 mt-1" />
              <p className="text-xs text-white/70 leading-relaxed">Adrigo Insights AI is fully PIPEDA compliant. Your data is entirely anonymized, and voice prints are strictly used for transcription verification.</p>
            </div>
          </div>
          <div className="text-xs text-white/40">© {new Date().getFullYear()} Adrigo Insights AI Inc.<br/>Halifax, Nova Scotia, Canada</div>
        </div>
      </footer>
    </div>
  );
}
